import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { FilePlus2, UserPlus, Search, Flag, X, FolderOpen, FileClock, LogIn } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, errMsg, openBlob } from "@/lib/api";
import { FOLDERS, FOLDER_COLORS } from "@/lib/status";
import { usePractice } from "@/pages/StaffApp";
import PacketList from "@/components/PacketList";
import ReadingPane from "@/components/ReadingPane";
import AddPatientDrawer from "@/components/AddPatientDrawer";
import NewPacketDialog from "@/components/NewPacketDialog";
import PatientChartSheet from "@/components/PatientChartSheet";
import FindPatientDialog from "@/components/FindPatientDialog";
import { DateFilter, dateParams, dateLabel } from "@/components/DateFilter";

function FolderNav({ folder, setFolder, counts }) {
  const { practice, can } = usePractice();
  const dl = (path, name) => openBlob(path, name).catch((e) => toast.error(errMsg(e)));
  return (
    <aside className="hidden lg:flex w-64 shrink-0 border-r bg-slate-50/70 flex-col" data-testid="folder-nav">
      <div className="p-4 border-b text-white relative overflow-hidden" style={{ background: "linear-gradient(135deg, var(--brand), #0F172A)" }}>
        <div className="absolute -right-6 -top-6 w-24 h-24 rounded-full bg-white/15 blur-xl" />
        <p className="text-[11px] uppercase tracking-wider text-white/75">Active organization</p>
        <p className="font-bold leading-tight mt-1" data-testid="practice-identity-name">{practice.name}</p>
        <p className="text-xs text-white/80 mt-1">{practice.legal_entity}</p>
      </div>
      <nav className="p-2 overflow-y-auto space-y-0.5">
        {FOLDERS.map(({ key, label, icon: Icon }) => (
          <button key={key} onClick={() => setFolder(key)} data-testid={`folder-${key}`} style={{ "--fc": FOLDER_COLORS[key] }}
            className={`folder-btn w-full flex items-center gap-3 px-2.5 h-11 rounded-lg text-sm transition-[background-color,box-shadow] duration-150 ${folder === key ? "bg-white font-bold shadow-sm ring-1 ring-slate-200 text-slate-900" : "text-slate-700 hover:bg-white/80"}`}>
            <span className="folder-ico w-7 h-7 rounded-md grid place-items-center shrink-0"><Icon className="w-4 h-4" /></span><span className="flex-1 text-left">{label}</span>
            <span className="folder-pill text-xs tabular-nums px-2 py-0.5 rounded-full font-bold" data-testid={`folder-count-${key}`}>{counts[key] ?? ""}</span>
          </button>
        ))}
      </nav>
      {(can("report:read") || can("member:manage")) && <div className="mt-auto p-2 border-t space-y-1" data-testid="folder-nav-downloads">
        <p className="px-3 pt-1 text-[11px] uppercase tracking-wider text-slate-500">Timestamp reports (PDF)</p>
        {can("report:read") && <button onClick={() => dl("/reports/signing-timestamps.pdf?days=30", "signing-timestamps.pdf")} data-testid="download-signing-timestamps"
          className="w-full flex items-center gap-3 px-3 h-10 rounded-md text-sm text-slate-700 hover:bg-slate-50"><FileClock className="w-4 h-4" />Signing timestamps</button>}
        {can("member:manage") && <button onClick={() => dl("/reports/login-history.pdf?days=30", "login-history.pdf")} data-testid="download-login-history"
          className="w-full flex items-center gap-3 px-3 h-10 rounded-md text-sm text-slate-700 hover:bg-slate-50"><LogIn className="w-4 h-4" />Staff login history</button>}
      </div>}
    </aside>
  );
}

export default function Workspace() {
  const { can, practice, facility, role, scope } = usePractice();
  const loc = useLocation();
  const [folder, setFolder] = useState(loc.state?.folder || "my_work");
  const [counts, setCounts] = useState({});
  const [q, setQ] = useState("");
  const [query, setQuery] = useState("");
  const [flagged, setFlagged] = useState(false);
  const [provider, setProvider] = useState("");
  const [items, setItems] = useState([]);
  const [cursor, setCursor] = useState(null);
  const [sel, setSel] = useState(loc.state?.sel || null);
  const [pidSeen, setPidSeen] = useState(practice.id);
  if (pidSeen !== practice.id) { setPidSeen(practice.id); setSel(null); }
  const [checked, setChecked] = useState([]);
  const [addOpen, setAddOpen] = useState(false);
  const [findOpen, setFindOpen] = useState(false);
  const [packetFor, setPacketFor] = useState(undefined);
  const [chartId, setChartId] = useState(null);
  const [dateSel, setDateSel] = useState({ kind: "all" });
  const [basis, setBasis] = useState("visit");
  const dp = dateParams(dateSel, basis);
  const created = dp.created || "";
  const visit = dp.visit || "";

  const loadCounts = useCallback(() => api.get("/folders", { params: { facility, created, visit } }).then((r) => setCounts(r.data)).catch(() => {}), [facility, created, visit]);
  const loaded = useRef(0);
  const seq = useRef(0);
  const load = useCallback(async (more, keep = 0) => {
    const my = more ? seq.current : ++seq.current;
    try {
      const base = { folder: query ? "all" : folder, q: query, flagged, provider, facility, created, visit };
      let { data } = await api.get("/packets", { params: { ...base, cursor: more || "" } });
      let rows = data.items;
      while (keep > rows.length && data.next_cursor && my === seq.current) {
        ({ data } = await api.get("/packets", { params: { ...base, cursor: data.next_cursor, limit: 100 } }));
        rows = [...rows, ...data.items];
      }
      if (my !== seq.current) { return; }
      setItems((prev) => { const next = more ? [...prev, ...rows.filter((r) => !prev.some((x) => x.id === r.id))] : rows; loaded.current = next.length; return next; });
      setCursor(data.next_cursor);
    } catch (e) { toast.error(errMsg(e)); }
  }, [folder, query, flagged, provider, facility, created, visit]);
  const refresh = useCallback(() => { load(); loadCounts(); }, [load, loadCounts]);
  useEffect(() => { refresh(); setChecked([]); }, [refresh]);
  useEffect(() => {
    const t = setInterval(() => { if (document.visibilityState === "visible") { load(null, loaded.current); loadCounts(); } }, 30000);
    return () => clearInterval(t);
  }, [load, loadCounts]);

  const chips = [query && { k: "q", l: `"${query}" in all folders`, clear: () => { setQ(""); setQuery(""); } },
    flagged && { k: "f", l: "Flagged", clear: () => setFlagged(false) },
    provider && { k: "p", l: practice.providers.find((p) => p.code === provider)?.name, clear: () => setProvider("") },
    dateSel.kind !== "all" && { k: "d", l: `${basis === "visit" ? "Visit/due" : "Created"}: ${dateSel.kind === "days" || dateSel.kind === "range" ? dateLabel(dateSel) : dateSel.kind}`, clear: () => setDateSel({ kind: "all" }) }].filter(Boolean);

  return (
    <div className="h-full flex flex-col">
      {scope?.limited && !scope.providers.length && !scope.facilities.length && (
        <div className="shrink-0 bg-amber-50 border-b border-amber-200 text-amber-900 text-sm px-4 py-2" data-testid="workspace-no-scope">
          No providers or facilities are assigned to you yet, so there is nothing to show. Ask your organization administrator to set your group (Admin → Members).
        </div>)}
      <div className="h-14 shrink-0 border-b bg-white flex items-center gap-2 px-3 overflow-x-auto" data-testid="workspace-command-bar">
        {can("packet:write") && <Button className="btn-brand h-10" onClick={() => setPacketFor(null)} data-testid="cmd-new-packet"><FilePlus2 className="w-4 h-4 mr-1" />New Packet</Button>}
        {can("patient:create") && <Button variant="outline" className="h-10" onClick={() => setAddOpen(true)} data-testid="cmd-add-patient"><UserPlus className="w-4 h-4 mr-1" />Add Patient</Button>}
        <Button variant="outline" className="h-10" onClick={() => setFindOpen(true)} data-testid="cmd-open-chart"><FolderOpen className="w-4 h-4 mr-1" />Open Chart</Button>
        <form className="ml-auto flex items-center gap-2" onSubmit={(e) => { e.preventDefault(); setQuery(q); }}>
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-3 text-slate-400" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Name, DOB, chart or EHR ID" aria-label="Search packets"
              className="pl-9 h-10 w-56 lg:w-72" data-testid="packet-search-input" />
          </div>
          <Button type="submit" variant="outline" className="h-10" data-testid="packet-search-btn">Search</Button>
        </form>
      </div>
      <div className="flex-1 min-h-0 flex">
        <FolderNav folder={folder} setFolder={(f) => { setFolder(f); setSel(null); }} counts={counts} />
        <section className={`${sel ? "hidden lg:flex" : "flex"} w-full lg:w-[400px] shrink-0 border-r bg-white flex-col min-h-0`}>
          <div className="p-3 border-b space-y-2">
            <div className="lg:hidden">
              <Select value={folder} onValueChange={(f) => setFolder(f)}>
                <SelectTrigger className="h-11" data-testid="folder-select-mobile"><SelectValue /></SelectTrigger>
                <SelectContent>{FOLDERS.map((f) => <SelectItem key={f.key} value={f.key}>{f.label} ({counts[f.key] ?? 0})</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2">
              <Button variant={flagged ? "default" : "outline"} size="sm" className="h-9" onClick={() => setFlagged(!flagged)} data-testid="filter-flagged"><Flag className="w-3.5 h-3.5 mr-1" />Flagged</Button>
              {role !== "provider" && <Select value={provider || "all"} onValueChange={(v) => setProvider(v === "all" ? "" : v)}>
                <SelectTrigger className="h-9 flex-1" data-testid="filter-provider"><SelectValue placeholder="All providers" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All providers</SelectItem>
                  {practice.providers.map((p) => <SelectItem key={p.code} value={p.code}>{p.name}</SelectItem>)}
                </SelectContent>
              </Select>}
            </div>
            <DateFilter value={dateSel} onChange={setDateSel} basis={basis} setBasis={setBasis} countParams={{ folder: query ? "all" : folder, q: query, provider, facility, flagged }} />
            {chips.length > 0 && (
              <div className="flex flex-wrap gap-1" data-testid="filter-chips">
                {chips.map((c) => <span key={c.k} className="text-xs bg-slate-100 rounded-full pl-2 pr-1 py-0.5 flex items-center gap-1">{c.l}<button aria-label="Remove filter" onClick={c.clear}><X className="w-3 h-3" /></button></span>)}
                <button className="text-xs underline text-slate-500" onClick={() => chips.forEach((c) => c.clear())} data-testid="clear-filters-btn">Clear filters</button>
              </div>
            )}
          </div>
          <PacketList items={items} sel={sel} setSel={setSel} checked={checked} setChecked={setChecked}
            onMore={cursor ? () => load(cursor) : null} onChanged={refresh} basis={dateSel.kind === "all" ? "" : basis} />
        </section>
        <main className={`${sel ? "flex" : "hidden lg:flex"} flex-1 min-w-0 flex-col bg-slate-50`}>
          <ReadingPane id={sel} onBack={() => setSel(null)} onChanged={refresh} onOpenChart={setChartId} />
        </main>
      </div>
      <AddPatientDrawer open={addOpen} onOpenChange={setAddOpen} onOpenChart={setChartId}
        onSaved={(p, next) => { refresh(); if (next) setPacketFor(p); else setChartId(p.id); }}
        onPacketCreated={(id) => { refresh(); setSel(id); }} />
      {packetFor !== undefined && <NewPacketDialog patient={packetFor} onClose={() => setPacketFor(undefined)}
        onAddPatient={() => setAddOpen(true)} onCreated={(id) => { setPacketFor(undefined); refresh(); setSel(id); }} />}
      <FindPatientDialog open={findOpen} onOpenChange={setFindOpen} onPick={(p) => { setFindOpen(false); setChartId(p.id); }}
        onAdd={() => { setFindOpen(false); setAddOpen(true); }} />
      <PatientChartSheet id={chartId} onClose={() => setChartId(null)} onNewPacket={(p) => { setChartId(null); setPacketFor(p); }}
        onOpenPacket={(id) => { setChartId(null); setSel(id); }} />
    </div>
  );
}
